import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoltLightning,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { hashString } from "../../utils/colorUtils";
import { workerAvatarGradientColors } from "../../utils/workerAvatarColors";
import {
  getWorkerAvatarDicebearSrc,
  isWorkerAvatarDicebearDesign,
  WORKER_AVATAR_BOLT_DESIGN,
  WORKER_AVATAR_DEFAULT_DESIGN,
  type WorkerAvatarDesign,
} from "../../utils/workerAvatarDicebear";

interface WorkerAvatarProps {
  seed: string;
  size?: number;
  animated?: boolean;
  user?: boolean;
  image?: string;
  className?: string;
  colors?: { color1: string; color2: string };
  /** Seed gradient + Waves overlay (default), bolt, or other Dicebear preview styles. */
  design?: WorkerAvatarDesign;
}

// Helper function to generate initials from a seed string
function generateInitials(seed: string): string {
  // Split the seed by common separators and take the first character of each word
  const words = seed.split(/[\s\-_.]+/).filter((word) => word.length > 0);

  if (words.length === 0) {
    return seed.charAt(0).toUpperCase();
  }

  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  // Take first character of first and last word
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

export default function WorkerAvatar({
  seed,
  size = 48,
  animated = false,
  user = false,
  image,
  className,
  colors,
  design = WORKER_AVATAR_DEFAULT_DESIGN,
}: WorkerAvatarProps) {
  const safeSeed = seed || "default";
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(image && !imageFailed);

  useEffect(() => {
    setImageFailed(false);
  }, [image]);

  const dicebearSrc = useMemo(() => {
    if (design === WORKER_AVATAR_BOLT_DESIGN || user || showImage) return null;
    if (!isWorkerAvatarDicebearDesign(design)) return null;
    return getWorkerAvatarDicebearSrc(safeSeed, design, size);
  }, [design, safeSeed, showImage, size, user]);
  const showDicebearOverlay = dicebearSrc !== null;

  // If image is provided, render it as a simple image
  if (showImage) {
    return (
      <img
        src={image}
        alt={`Avatar for ${seed}`}
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onError={() => setImageFailed(true)}
        className={`rounded-full object-cover ${className || ""}`}
        style={{ width: size, height: size }}
      />
    );
  }

  const { color1, color2 } = colors
    ? workerAvatarGradientColors(safeSeed, undefined, colors)
    : workerAvatarGradientColors(safeSeed);
  const hash = hashString(safeSeed);

  // Sanitize seed to create a valid HTML ID
  const sanitizedSeed = safeSeed.replace(/[^a-zA-Z0-9-]/g, "");
  const gradientId = `avatar-gradient-${sanitizedSeed}`;
  const filterId = `avatar-filter-${sanitizedSeed}`;
  const maskId = `avatar-mask-${sanitizedSeed}`;
  const monogramShadowFilterId = `avatar-monogram-shadow-${sanitizedSeed}`;

  // Animation properties for circular motion
  const animRadius = Math.max(8, 15 + (hash % 10)); // Ensure minimum radius of 8, max 24
  const animDur = Math.max(3, 5 + ((hash >> 2) % 5)); // Ensure minimum duration of 3s, max 9s

  // More variation in initial hotspot position
  const p1 = {
    x: 20 + (hash % 61), // 20–80
    y: 20 + ((hash >> 3) % 61), // 20–80
  };
  const p2 = { x: Math.min(90, 50 + animRadius), y: 50 };
  const p3 = { x: 50, y: Math.min(90, 50 + animRadius) };
  const p4 = { x: Math.max(10, 50 - animRadius), y: 50 };

  // Ensure we have distinct points for animation
  const hasMovement =
    p1.x !== p2.x || p1.y !== p2.y || p2.x !== p3.x || p2.y !== p3.y;

  const cxValues = `${p1.x}%; ${p2.x}%; ${p3.x}%; ${p4.x}%; ${p1.x}%`;
  const cyValues = `${p1.y}%; ${p2.y}%; ${p3.y}%; ${p4.y}%; ${p1.y}%`;

  // Randomize gradient properties for a "watercolor" feel
  const r = 50 + ((hash >> 3) % 20); // 50 to 69

  // Randomize filter properties for a "streaky paint" feel
  const stdDeviation = 0.8 + ((hash >> 4) % 5) / 10; // 0.8 to 1.2
  const baseFrequency = (0.02 + (hash % 15) / 1000).toFixed(4); // 0.02 to 0.034
  const numOctaves = 2 + (hash % 2); // 2 or 3
  const scale = 30 + ((hash >> 2) % 15); // 30 to 44

  const initials = generateInitials(safeSeed);
  const showMonogramOverlay =
    !showImage && initials.length > 0 && design !== "initials";

  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-full ${className || ""}`}
      style={{ width: size, height: size }}
    >
      <svg
        key={seed}
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="block h-full w-full"
        style={{ display: showImage ? "none" : "block" }}
      >
        <defs>
          <radialGradient
            id={gradientId}
            cx={`${p1.x}%`}
            cy={`${p1.y}%`}
            r={`${r}%`}
          >
            {animated && hasMovement && (
              <>
                <animate
                  attributeName="cx"
                  values={cxValues}
                  dur={`${animDur}s`}
                  repeatCount="indefinite"
                  begin="0s"
                  calcMode="spline"
                  keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
                />
                <animate
                  attributeName="cy"
                  values={cyValues}
                  dur={`${animDur}s`}
                  repeatCount="indefinite"
                  begin="0s"
                  calcMode="spline"
                  keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
                />
              </>
            )}
            <stop stopColor={color1} stopOpacity="0.72" />
            <stop offset="100%" stopColor={color2} />
          </radialGradient>
          <filter id={filterId}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency={baseFrequency}
              numOctaves={numOctaves}
              stitchTiles="stitch"
              result="turbulence"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="turbulence"
              scale={scale}
              xChannelSelector="R"
              yChannelSelector="G"
              result="displaced"
            />
            <feGaussianBlur in="displaced" stdDeviation={stdDeviation} />
          </filter>
          <mask id={maskId}>
            <circle cx="50" cy="50" r="50" fill="white" />
          </mask>
        </defs>
        <g mask={`url(#${maskId})`}>
          <circle cx="50" cy="50" r="50" fill={color2} />
          <rect
            x="-10"
            y="-10"
            width="120"
            height="120"
            fill={`url(#${gradientId})`}
            filter={`url(#${filterId})`}
          >
            {animated && !hasMovement && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 50 50; 360 50 50"
                dur={`${animDur}s`}
                repeatCount="indefinite"
                begin="0s"
              />
            )}
          </rect>
        </g>
      </svg>
      {showDicebearOverlay && (
        <img
          src={dicebearSrc}
          alt=""
          aria-hidden="true"
          width={size}
          height={size}
          className="pointer-events-none absolute inset-0 h-full w-full brightness-[0.92]"
        />
      )}
      {showMonogramOverlay ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          aria-hidden="true"
        >
          <defs>
            <filter
              id={monogramShadowFilterId}
              x="-30%"
              y="-30%"
              width="160%"
              height="160%"
            >
              <feDropShadow
                dx="0"
                dy="1.2"
                stdDeviation="1"
                floodColor="#000000"
                floodOpacity="0.35"
              />
            </filter>
          </defs>
          <text
            x="50"
            y="50"
            textAnchor="middle"
            dominantBaseline="central"
            fill="#ffffff"
            fontSize={initials.length > 1 ? 36 : 42}
            fontWeight={600}
            letterSpacing={initials.length > 1 ? "0.06em" : "0"}
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
            filter={`url(#${monogramShadowFilterId})`}
          >
            {initials}
          </text>
        </svg>
      ) : null}
      {!showMonogramOverlay &&
        !user &&
        !showDicebearOverlay &&
        design === WORKER_AVATAR_BOLT_DESIGN && (
          <div className="absolute inset-0 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faBoltLightning}
              className="rotate-6 text-white"
              style={{
                fontSize: `${size * 0.5}px`,
                mixBlendMode: "overlay",
              }}
            />
          </div>
        )}
    </div>
  );
}
